import { useMemo } from "react"

import { Button, Select } from "@productlane/ui"

interface ContactsImageUrlStepProps {
	data: string[][]
	hasHeaders: boolean
	back: () => void
	next: () => void
	imageUrlIndex: number
	setImageUrlIndex: (index: number) => void
	sortedData: string[][]
}

export function ContactsImageUrlStep({
	data,
	hasHeaders,
	back,
	next,
	imageUrlIndex,
	setImageUrlIndex,
	sortedData,
}: ContactsImageUrlStepProps) {
	const headers = useMemo(() => {
		if (!data[0]) {
			return []
		}
		if (hasHeaders) {
			return data[0].map((it, i) => ({
				label: it,
				value: i.toString(),
			}))
		}
		return data[0].map((_, i) => ({
			label: `${i + 1}. Column`,
			value: i.toString(),
		}))
	}, [hasHeaders, data])

	const preview = useMemo(() => {
		if (imageUrlIndex === -1) return []
		const col = sortedData[imageUrlIndex] ?? []
		return col.slice(hasHeaders ? 1 : 0, hasHeaders ? 4 : 3)
	}, [hasHeaders, imageUrlIndex, sortedData])

	return (
		<div>
			<Select
				value={imageUrlIndex.toString()}
				label="Select the column of the contacts image URL (optional)"
				name="contact-image-url"
				options={[emptyState("No image URL"), ...headers]}
				onChange={(it) => setImageUrlIndex(Number(it))}
			/>

			<div className="mb-2 mt-4 text-sm text-primary">Preview:</div>
			<div className="relative mt-1 overflow-hidden rounded-lg border bg-white p-2 dark:bg-gray-900">
				{imageUrlIndex === -1 ? (
					<div className="text-xs italic text-secondary">No Preview</div>
				) : (
					<div className="flex flex-col gap-1">
						{preview.map((it, i) => (
							<div key={`img-${i}`} className="truncate text-xs text-secondary">
								{it}
							</div>
						))}
					</div>
				)}
				{imageUrlIndex !== -1 && (
					<div className="absolute inset-x-0 bottom-0 h-[80%] bg-gradient-to-b from-transparent via-white/80 to-white dark:via-brand-900/80 dark:to-brand-900" />
				)}
			</div>

			<div className="mt-2 flex items-center justify-between">
				<Button variant="link" onClick={back}>
					Back
				</Button>
				<Button onClick={next}>Select</Button>
			</div>
		</div>
	)
}

const emptyState = (label: string) => ({
	label,
	value: "-1",
})
